import React, { useEffect, useState } from 'react';
import api from '../api/api';
import '../pages/directBooking.css';

const EMPTY_RULE = { label: '', startDate: '', endDate: '', nightlyRate: '' };

const MONTH_NAMES = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DOW_LABELS = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const toDateStr = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const PricingCalendar = ({ pricingRules, baseRate }) => {
    const today = new Date();
    const [viewYear, setViewYear] = useState(today.getFullYear());
    const [viewMonth, setViewMonth] = useState(today.getMonth());

    const toPrev = () => {
        if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); }
        else setViewMonth(m => m - 1);
    };
    const toNext = () => {
        if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); }
        else setViewMonth(m => m + 1);
    };

    const getRateInfo = (date) => {
        const dateStr = toDateStr(date);
        for (const rule of pricingRules) {
            const start = rule.start_date || rule.startDate || '';
            const end = rule.end_date || rule.endDate || '';
            if (dateStr >= start && dateStr < end) {
                return {
                    rate: Number(rule.nightly_rate || rule.nightlyRate || 0),
                    label: rule.label || ''
                };
            }
        }
        return null;
    };

    const firstDow = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const todayStr = toDateStr(today);

    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);

    return (
        <div className="pricing-calendar">
            <div className="pricing-calendar-nav">
                <button type="button" className="pricing-cal-nav-btn" onClick={toPrev}>&#8249;</button>
                <span className="pricing-cal-title">{MONTH_NAMES[viewMonth]} {viewYear}</span>
                <button type="button" className="pricing-cal-nav-btn" onClick={toNext}>&#8250;</button>
            </div>
            <div className="pricing-calendar-grid">
                {DOW_LABELS.map(d => <div key={d} className="pricing-cal-dow">{d}</div>)}
                {cells.map((day, idx) => {
                    if (day === null) return <div key={`blank-${idx}`} className="pricing-cal-cell pricing-cal-blank" />;
                    const date = new Date(viewYear, viewMonth, day);
                    const rateInfo = getRateInfo(date);
                    const cellDateStr = toDateStr(date);
                    const isToday = cellDateStr === todayStr;
                    return (
                        <div
                            key={day}
                            className={`pricing-cal-cell${rateInfo ? ' pricing-cal-has-rule' : ''}${isToday ? ' pricing-cal-today' : ''}`}
                            title={rateInfo ? `${rateInfo.label ? rateInfo.label + ' — ' : ''}$${rateInfo.rate.toFixed(2)}/night` : baseRate > 0 ? `Base rate — $${Number(baseRate).toFixed(2)}/night` : ''}
                        >
                            <span className="pricing-cal-day-num">{day}</span>
                            {rateInfo ? (
                                <span className="pricing-cal-rate">${rateInfo.rate % 1 === 0 ? rateInfo.rate : rateInfo.rate.toFixed(2)}</span>
                            ) : baseRate > 0 ? (
                                <span className="pricing-cal-base-rate">${Number(baseRate) % 1 === 0 ? Number(baseRate) : Number(baseRate).toFixed(2)}</span>
                            ) : null}
                        </div>
                    );
                })}
            </div>
            <div className="pricing-cal-legend">
                <span className="pricing-cal-legend-rule"><span className="pricing-cal-legend-swatch pricing-cal-legend-swatch-rule"></span>Override rate</span>
                {baseRate > 0 && <span className="pricing-cal-legend-base"><span className="pricing-cal-legend-swatch pricing-cal-legend-swatch-base"></span>Base rate (${Number(baseRate) % 1 === 0 ? Number(baseRate) : Number(baseRate).toFixed(2)})</span>}
            </div>
        </div>
    );
};

const DirectBookingPricingRules = ({ propertyId, compact = false, baseRate = 0 }) => {
    const [pricingRules, setPricingRules] = useState([]);
    const [newRule, setNewRule] = useState(EMPTY_RULE);
    const [savingRule, setSavingRule] = useState(false);
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });
    const [showCalendar, setShowCalendar] = useState(!compact);

    useEffect(() => {
        let isMounted = true;

        const loadPricingRules = async () => {
            if (!propertyId) {
                if (isMounted) {
                    setPricingRules([]);
                    setLoading(false);
                }
                return;
            }

            setLoading(true);
            try {
                const response = await api.get(`/direct-booking/host/listings/property/${propertyId}/pricing-rules`);
                if (!isMounted) return;
                setPricingRules(Array.isArray(response?.data) ? response.data : []);
                setMessage({ type: '', text: '' });
            } catch (e) {
                if (!isMounted) return;
                setPricingRules([]);
                setMessage({ type: 'error', text: 'Failed to load pricing rules.' });
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        loadPricingRules();

        return () => {
            isMounted = false;
        };
    }, [propertyId]);

    const updateRuleField = (field, value) => {
        setNewRule((prev) => ({
            ...prev,
            [field]: value
        }));
    };

    const addRule = async () => {
        if (!newRule.startDate || !newRule.endDate || !newRule.nightlyRate) {
            setMessage({ type: 'error', text: 'Start date, end date, and nightly rate are required for a pricing rule.' });
            return;
        }

        setSavingRule(true);
        try {
            const response = await api.post(`/direct-booking/host/listings/property/${propertyId}/pricing-rules`, {
                label: newRule.label,
                startDate: newRule.startDate,
                endDate: newRule.endDate,
                nightlyRate: Number(newRule.nightlyRate)
            });
            setPricingRules((prev) => [...prev, response.data]);
            setNewRule(EMPTY_RULE);
            setMessage({ type: 'success', text: 'Pricing rule added.' });
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to add pricing rule.' });
        } finally {
            setSavingRule(false);
        }
    };

    const deleteRule = async (ruleId) => {
        try {
            await api.delete(`/direct-booking/host/listings/property/${propertyId}/pricing-rules/${ruleId}`);
            setPricingRules((prev) => prev.filter((rule) => rule.id !== ruleId));
            setMessage({ type: 'success', text: 'Pricing rule deleted.' });
        } catch (e) {
            setMessage({ type: 'error', text: e?.response?.data?.error || 'Failed to delete rule.' });
        }
    };

    return (
        <div className={`direct-booking-pricing-rules${compact ? ' direct-booking-pricing-rules-compact' : ''}`}>
            <h3>Dynamic Pricing Rules</h3>
            <p className="direct-booking-rules-hint">Override the nightly rate for specific date ranges (e.g. holidays, peak season). Rules are applied per night; the base rate is used for nights not covered by any rule.</p>

            {message.text && <div className={`direct-booking-alert ${message.type === 'success' ? 'direct-booking-alert-success' : 'direct-booking-alert-error'}`}>{message.text}</div>}

            {loading ? (
                <p className="direct-booking-rules-hint">Loading pricing rules...</p>
            ) : pricingRules.length > 0 ? (
                <table className="direct-booking-rules-table">
                    <thead>
                        <tr><th>Label</th><th>From</th><th>To</th><th>Rate/night</th><th></th></tr>
                    </thead>
                    <tbody>
                        {pricingRules.map((rule) => (
                            <tr key={rule.id}>
                                <td>{rule.label || '—'}</td>
                                <td>{rule.start_date || rule.startDate}</td>
                                <td>{rule.end_date || rule.endDate}</td>
                                <td>${Number(rule.nightly_rate || rule.nightlyRate || 0).toFixed(2)}</td>
                                <td>
                                    <button type="button" className="btn-danger-sm" onClick={() => deleteRule(rule.id)}>Delete</button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            ) : (
                <p className="direct-booking-rules-hint">No pricing rules yet. Add one below to override the nightly rate for specific dates.</p>
            )}

            <div className="pricing-calendar-toggle-row">
                <button type="button" className="btn-secondary pricing-cal-toggle-btn" onClick={() => setShowCalendar(v => !v)}>
                    {showCalendar ? 'Hide Calendar' : 'Show Pricing Calendar'}
                </button>
            </div>
            {showCalendar && (
                <PricingCalendar pricingRules={pricingRules} baseRate={baseRate} />
            )}

            <div className="direct-booking-rules-form">
                <h4>Add a Rule</h4>
                <div className="direct-booking-grid">
                    <label>
                        Label (optional)
                        <input value={newRule.label} onChange={(e) => updateRuleField('label', e.target.value)} placeholder="e.g. Holiday Weekend" />
                    </label>
                    <label>
                        Rate / night (USD)
                        <input type="number" step="0.01" value={newRule.nightlyRate} onChange={(e) => updateRuleField('nightlyRate', e.target.value)} />
                    </label>
                    <label>
                        Start Date
                        <input type="date" value={newRule.startDate} onChange={(e) => updateRuleField('startDate', e.target.value)} />
                    </label>
                    <label>
                        End Date <span className="direct-booking-rules-hint-inline">(exclusive — last date not included)</span>
                        <input type="date" value={newRule.endDate} onChange={(e) => updateRuleField('endDate', e.target.value)} />
                    </label>
                </div>
                <button type="button" className="btn-primary" disabled={savingRule} onClick={addRule}>{savingRule ? 'Saving...' : 'Add Rule'}</button>
            </div>
        </div>
    );
};

export default DirectBookingPricingRules;